import { InputFileUpload, InputSelect } from "@components-common/forms";
import { useUserContext } from "../../../_runtime/iam/session.context";
import { Formik, useField, useFormikContext } from "formik";
import { FormProvider } from "../../../components-common/forms/provider";
import { Col, Loader } from "taltech-styleguide";
import { application } from "../../../_runtime/bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { mapDataToDropdownOptions } from "@components-domain/generation";
import { useNotifications } from "@hooks";
import { Toolbar } from "@components-common/toolbar";

/**
 * AssignmentSubmitFormContent Component
 *
 * Renders the content for the assignment submission form.
 *
 * @param {Object} props - Component properties.
 * @param {Array} props.buildConfigsList - List of building configurations.
 * @returns {JSX.Element} - The rendered form content.
 */
export const AssignmentSubmitFormContent = ({
  buildConfigsList,
  onFileChange,
}: any) => {
  const formik = useFormikContext();
  const list = mapDataToDropdownOptions(buildConfigsList, "id", "name");
  const [fieldBuildConfig, helpersBuildConfig, metaBuildConfig] = useField(
    `buildingConfigurationId`
  );

  const left = [
    { id: "submit", label: "Submit", onAction: () => formik.submitForm() },
  ];

  useEffect(() => {
    if (buildConfigsList.length > 0) {
      metaBuildConfig.setValue(buildConfigsList[0].id);
    }
  }, []);

  return (
    <>
      <Toolbar left={left} right={["back"]} />
      <Col md={6} className="mt-3">
        <InputSelect
          id="buildingConfigurationId"
          label="Building Configuration"
          options={list}
          elementClass="mb-3"
        />
        {buildConfigsList.length === 0 && (
          <span className="text-danger">
            The assignment must have at least 1 building configuration to be
            able to submit.
          </span>
        )}
        <InputFileUpload
          type="FILE"
          handleFileFromOutside={true}
          setFile={onFileChange}
        />
        {/* <input type="file" onChange={onFileChange} /> */}
        {/* <FeedbackMessage fieldId="fileName" /> */}
      </Col>
    </>
  );
};

/**
 * AssignmentSubmitForm Component
 *
 * Manages the state and submission logic for the assignment submission form.
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.empty - The initial values for the form.
 * @returns {JSX.Element} - The rendered form component.
 */
export const AssignmentSubmitForm = (props: any) => {
  const { empty } = props;
  const nav = useNavigate();
  const userNotifications = useNotifications();
  const { userId } = useUserContext();
  const { assignmentId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [buildConfigsList, setBuildConfigsList] = useState<any[] | undefined>(
    undefined
  );
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    application
      .getHttpClient()
      .get(`buildingsconfigms/configurations/GetConfigurationEntries?subjectId=${assignmentId}`)
      .then((response: any) => setBuildConfigsList(response.body.items))
      .catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    if (buildConfigsList) {
      setIsLoading(false);
    }
  }, [buildConfigsList]);

  /**
   * Handles form submission.
   *
   * @param {Object} values - The form values.
   */
  const onSubmit = async (values: any) => {
    if (buildConfigsList?.length === 0) return;

    var formData = new FormData();
    formData.append("UserId", userId?.toString() || "");
    formData.append("AssignmentId", assignmentId?.toString() || "");
    formData.append(
      "BuildingConfigurationId",
      values.buildingConfigurationId.toString()
    );
    formData.append(
      "ConfigurationPath",
      buildConfigsList!
        .find((x) => x.id === values.buildingConfigurationId)
        .url.toString()
    );
    formData.append("IsMain", "true");
    if (file) {
      formData.append("File", file);
    } else {
      userNotifications.error({ title: "You need to upload a file." });
      return;
    }

    setIsLoading(true);
    application
      .getHttpClient()
      .post("/coursesms/assignments/submissions/postWithStreaming", formData, {
        headers: {
          " Content-Type": "multipart/form-data",
        },
      }).then(() => nav(-1))
      .finally(() => setIsLoading(false));
  };

  return isLoading ? (
    <Loader fullScreen></Loader>
  ) : (
    <FormProvider>
      <Formik
        initialValues={empty}
        // validationSchema={assignmentSubmissionSchema}
        onSubmit={onSubmit}
      >
        <AssignmentSubmitFormContent
          buildConfigsList={buildConfigsList}
          onFileChange={setFile}
        />
      </Formik>
    </FormProvider>
  );
};